import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';
import LanguageIcon from '@mui/icons-material/Language';
import { TV_QR_IMAGE, TV_QR_URL } from '../../data/tvDemoDisplay';
import tvTheme from './tvTheme';

const EYECATCH_SRC = '/assets/eyecatch1.png';

/** 日別版 TVActionPanel と同じ尺・構成 */
const guideSizes = (compact) => ({
  title: compact ? '0.95rem' : 'clamp(1.6rem, 2.85vw, 2.45rem)',
  caption: compact ? '0.78rem' : 'clamp(1.05rem, 1.85vw, 1.45rem)',
  sectionHead: compact ? '0.84rem' : 'clamp(1.25rem, 1.9vw, 1.55rem)',
  audience: compact ? '0.68rem' : 'clamp(0.95rem, 1.45vw, 1.2rem)',
  body: compact ? '0.74rem' : 'clamp(1.15rem, 1.95vw, 1.55rem)',
  note: compact ? '0.58rem' : 'clamp(0.9rem, 1.3vw, 1.1rem)',
  qrLead: compact ? '0.82rem' : 'clamp(1.35rem, 2.15vw, 2rem)',
  qrUrl: compact ? '0.58rem' : 'clamp(0.95rem, 1.4vw, 1.28rem)',
});

function WebGuidePromo({ compact, qrUrl, description }) {
  const sizes = guideSizes(compact);
  const qrSize = compact ? 44 : 120;

  return (
    <Box
      sx={{
        flex: compact ? '0 0 auto' : '0 0 55%',
        minWidth: 0,
        height: compact ? undefined : '100%',
        m: 0,
        p: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 0,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          flex: compact ? '0 0 auto' : 1,
          minHeight: 0,
          display: 'flex',
          alignItems: 'flex-end',
          overflow: 'hidden',
          width: '100%',
        }}
      >
        <Box
          component="img"
          src={EYECATCH_SRC}
          alt=""
          sx={{ width: '100%', maxWidth: '100%', height: 'auto', display: 'block' }}
        />
      </Box>
      <Box
        sx={{
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: compact ? 0.25 : 0.5,
          px: 0.2,
          bgcolor: 'rgba(255,255,255,0.94)',
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{ fontSize: sizes.qrLead, fontWeight: 900, color: '#111', lineHeight: 1.15 }}
          >
            {description}
          </Typography>
          <Typography
            component="a"
            href={qrUrl}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              fontSize: sizes.qrUrl,
              fontWeight: 600,
              color: '#333',
              lineHeight: 1.15,
              textDecoration: 'none',
              wordBreak: 'break-all',
            }}
          >
            {qrUrl}
          </Typography>
        </Box>
        <Box
          component="img"
          src={TV_QR_IMAGE}
          alt="Webダッシュボード QRコード"
          sx={{ flexShrink: 0, width: qrSize, height: qrSize, display: 'block' }}
        />
      </Box>
    </Box>
  );
}

WebGuidePromo.propTypes = {
  compact: PropTypes.bool,
  qrUrl: PropTypes.string,
  description: PropTypes.string,
};

function TVWebGuidePanel({ data, compact = false, loading = false }) {
  if (!data) {
    return (
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography sx={{ fontWeight: 800, color: tvTheme.colors.textDark }}>
          {loading ? '読込中…' : 'データがありません'}
        </Typography>
      </Box>
    );
  }

  const sizes = guideSizes(compact);
  const qrUrl = data.qrUrl || TV_QR_URL;
  const purposeGroups = data.purposeGroups?.length
    ? data.purposeGroups
    : [{ audience: null, items: data.webPurposes || [] }];

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        gap: compact ? 0.12 : 0.32,
        flexDirection: compact ? 'column' : 'row',
        alignItems: 'stretch',
        overflow: 'hidden',
        opacity: loading ? 0.5 : 1,
      }}
    >
      <Box
        sx={{
          flex: compact ? '1 1 auto' : '1 1 45%',
          minWidth: 0,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'rgba(255,255,255,0.94)',
          border: '3px solid #fff',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            px: compact ? 0.3 : 0.45,
            py: compact ? 0.18 : 0.28,
            lineHeight: 1.08,
          }}
        >
          <Typography
            sx={{ fontSize: sizes.title, fontWeight: 900, color: tvTheme.colors.textDark }}
          >
            続きはWebで
          </Typography>
          {data.headline && (
            <Typography
              sx={{
                fontSize: sizes.caption,
                fontWeight: 800,
                color: tvTheme.colors.accentBlue,
                mt: compact ? 0.04 : 0.08,
              }}
            >
              {data.headline}
            </Typography>
          )}
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.35,
            px: compact ? 0.28 : 0.36,
            py: compact ? 0.16 : 0.22,
            background: `linear-gradient(90deg, ${tvTheme.colors.labelBar} 0%, #1976d2 100%)`,
            flexShrink: 0,
          }}
        >
          <LanguageIcon sx={{ color: '#fff', fontSize: compact ? 19 : 30 }} />
          <Typography
            sx={{ fontSize: sizes.sectionHead, fontWeight: 900, color: '#fff', lineHeight: 1.05 }}
          >
            こんなとき、Webで詳しく
          </Typography>
        </Box>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            px: compact ? 0.28 : 0.42,
            py: compact ? 0.14 : 0.22,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-evenly',
            gap: compact ? 0.18 : 0.28,
            overflow: 'hidden',
          }}
        >
          {purposeGroups.map((group) => (
            <Box key={group.audience || 'default'} sx={{ minWidth: 0 }}>
              {group.audience && (
                <Typography
                  sx={{
                    fontSize: sizes.audience,
                    fontWeight: 900,
                    color: tvTheme.colors.labelBar,
                    mb: compact ? 0.08 : 0.12,
                    letterSpacing: '0.04em',
                  }}
                >
                  {group.audience}
                </Typography>
              )}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: compact ? 0.1 : 0.14 }}>
                {(group.items || []).map((item) => (
                  <Typography
                    key={item}
                    sx={{
                      fontSize: sizes.body,
                      fontWeight: 800,
                      color: '#1a1a1a',
                      lineHeight: 1.2,
                      pl: compact ? 0.32 : 0.42,
                      borderLeft: `5px solid ${tvTheme.colors.labelBar}`,
                    }}
                  >
                    {item}
                  </Typography>
                ))}
              </Box>
            </Box>
          ))}
        </Box>

        {(data.disclaimer || data.generatedAtLabel) && (
          <Typography
            sx={{
              flexShrink: 0,
              fontSize: sizes.note,
              fontWeight: 700,
              color: '#3b5c7a',
              lineHeight: 1.3,
              px: compact ? 0.3 : 0.45,
              py: compact ? 0.1 : 0.18,
              borderTop: `2px solid ${tvTheme.colors.gridLine}`,
            }}
          >
            {data.disclaimer}
            {data.generatedAtLabel ? `（最終更新 ${data.generatedAtLabel}）` : ''}
          </Typography>
        )}
      </Box>

      <WebGuidePromo compact={compact} qrUrl={qrUrl} description={data.description} />
    </Box>
  );
}

TVWebGuidePanel.propTypes = {
  data: PropTypes.object,
  compact: PropTypes.bool,
  loading: PropTypes.bool,
};

export default TVWebGuidePanel;
